/**
 * TabBar 组件 — 替代 uniapp 的 <tn-tabbar>
 *
 * 原版：双 Tab (跑腿 + 我的)，底部固定，safe-area 适配。
 *
 * Props: 与原 tn-tabbar 一致的设计：
 *   - value / defaultValue 当前 Tab 索引
 *   - list 配置项
 *   - activeColor / inactiveColor
 *   - safeAreaInsetBottom 默认 true
 *
 * 用法：
 *   <TabBar value={tab} list={[...]} onChange={setTab} />
 */
"use client";

import { Bike, User } from "lucide-react";
import { cn } from "@/lib/utils";

export type TabBarItem = {
  title: string;
  icon: "run" | "user";
  // 图标+文字 + badge
};

export const TAB_LIST: TabBarItem[] = [
  { title: "跑腿", icon: "run" },
  { title: "我的", icon: "user" },
];

// 图鸟 UI 原版颜色
const ACTIVE_COLOR = "#08B5D2"; // tn-cool-bg-color-7
const INACTIVE_COLOR = "#AAAAAA"; // 图标灰
const TEXT_ACTIVE = "#838383"; // active 文字色
const TEXT_INACTIVE = "#AAAAAA";

export function TabBar({
  value,
  onChange,
  list = TAB_LIST,
}: {
  value: number;
  onChange: (index: number) => void;
  list?: TabBarItem[];
}) {
  return (
    <nav
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50",
        "bg-white border-t border-border",
        "flex items-stretch justify-around",
        "safe-area-bottom",
      )}
      style={{ height: "calc(60px + env(safe-area-inset-bottom, 0px))" }}
    >
      {list.map((item, index) => {
        const active = value === index;
        const color = active ? ACTIVE_COLOR : INACTIVE_COLOR;
        const textColor = active ? TEXT_ACTIVE : TEXT_INACTIVE;
        return (
          <button
            key={item.title}
            onClick={() => onChange(index)}
            className={cn(
              "flex-1 flex flex-col items-center justify-center gap-1",
              "transition-all duration-200 active:scale-95",
            )}
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          >
            {item.icon === "run" ? (
              <Bike
                size={26}
                strokeWidth={active ? 2.5 : 2}
                color={color}
              />
            ) : (
              <User
                size={26}
                strokeWidth={active ? 2.5 : 2}
                color={color}
              />
            )}
            <span
              className={cn(
                "text-xs transition-colors",
                active && "font-medium",
              )}
              style={{ color: textColor }}
            >
              {item.title}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
